import { useState, type FormEvent, type ReactNode } from 'react';
import { api, errorMessage } from '../api';
import { cssColor } from '../colors';
import type { Role, UserPatchBody, UserResponse, UserWithRole } from '../types';
import { ConfirmButton } from './ConfirmButton';
import { Modal } from './Modal';

export interface UserEditDialogProps {
  user: UserWithRole;
  roles: Role[];
  /** Eigenes Konto: darf nicht deaktiviert/gelöscht werden. */
  isSelf: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}

/** Benutzer bearbeiten: Profil, Rolle, Passwort, Löschen. */
export function UserEditDialog({
  user,
  roles,
  isSelf,
  onClose,
  onSaved,
  onError,
}: UserEditDialogProps): ReactNode {
  const [username, setUsername] = useState(user.username);
  const [displayName, setDisplayName] = useState(user.displayName);
  const [active, setActive] = useState(user.active);
  const [roleId, setRoleId] = useState(user.roleId ?? '');
  const [roleLocked, setRoleLocked] = useState(user.roleLocked);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const roleChanged = roleId !== (user.roleId ?? '');

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    if (username.trim() === '') {
      setError('Der Benutzername darf nicht leer sein.');
      return;
    }
    if (password !== '' && password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen haben.');
      return;
    }

    const body: UserPatchBody = {};
    if (username.trim() !== user.username) body.username = username.trim();
    if (displayName.trim() !== user.displayName) body.displayName = displayName.trim();
    if (active !== user.active) body.active = active;
    if (roleChanged) {
      body.roleId = roleId === '' ? null : roleId;
      body.roleLocked = roleLocked;
    } else if (roleLocked !== user.roleLocked) {
      body.roleLocked = roleLocked;
    }
    if (password !== '') body.password = password;

    if (Object.keys(body).length === 0) {
      setError('Es gibt keine Änderungen zum Speichern.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const result = await api.patch<UserResponse>(`/api/users/${user.id}`, body);
      onSaved(`Benutzer "${result.user?.username ?? user.username}" gespeichert.`);
      onClose();
    } catch (cause: unknown) {
      setError(errorMessage(cause));
      setBusy(false);
    }
  };

  const remove = async (): Promise<void> => {
    setDeleting(true);
    setError(null);
    try {
      await api.del<{ ok: boolean }>(`/api/users/${user.id}`);
      onSaved(`Benutzer "${user.username}" gelöscht.`);
      onClose();
    } catch (cause: unknown) {
      const message = errorMessage(cause);
      setError(message);
      onError(message);
      setDeleting(false);
    }
  };

  return (
    <Modal title={`Benutzer bearbeiten — ${user.username}`} onClose={onClose}>
      <form className="form-grid" onSubmit={(event) => void submit(event)}>
        <div className="form-row">
          <label className="field">
            <span>Benutzername</span>
            <input
              type="text"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
          </label>
          <label className="field">
            <span>Anzeigename</span>
            <input
              type="text"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </label>
        </div>

        <div className="form-row">
          <label className="field">
            <span>Rolle</span>
            <select value={roleId} onChange={(event) => setRoleId(event.target.value)}>
              <option value="">Keine Rolle</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.displayName} ({role.name})
                </option>
              ))}
            </select>
          </label>
          <span
            className="color-dot color-dot-large"
            style={{ backgroundColor: cssColor(roles.find((role) => role.id === roleId)?.color) }}
            title="Rollenfarbe"
          />
        </div>

        <div className="form-row form-row-checks">
          <label className="check">
            <input
              type="checkbox"
              checked={active}
              disabled={isSelf}
              onChange={(event) => setActive(event.target.checked)}
            />
            <span>Aktiv {isSelf && <span className="muted small">(eigenes Konto)</span>}</span>
          </label>

          <label className="check">
            <input
              type="checkbox"
              checked={roleLocked}
              disabled={!roleChanged}
              onChange={(event) => setRoleLocked(event.target.checked)}
            />
            <span>
              Rolle gesperrt
              <span className="muted small">
                {roleChanged
                  ? ' — verhindert das Überschreiben durch den Cloud-Sync'
                  : ' — beim Rollenwechsel setzbar'}
              </span>
            </span>
          </label>
        </div>

        <label className="field">
          <span>Passwort zurücksetzen (leer = unverändert, min. 8 Zeichen)</span>
          <input
            type="password"
            value={password}
            autoComplete="new-password"
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        <dl className="detail-meta detail-meta-flat">
          <div>
            <dt>Quelle</dt>
            <dd>{user.source}</dd>
          </div>
          <div>
            <dt>Rechte</dt>
            <dd>{user.permissions.length > 0 ? user.permissions.join(', ') : '—'}</dd>
          </div>
          <div>
            <dt>Passwort gesetzt</dt>
            <dd>{user.hasPassword ? 'ja' : 'nein'}</dd>
          </div>
        </dl>

        {error !== null && (
          <div className="feedback feedback-error" role="alert">
            <span className="feedback-icon" aria-hidden="true">
              !
            </span>
            <span>{error}</span>
          </div>
        )}

        <div className="form-actions form-actions-split">
          <span>
            {!isSelf && (
              <ConfirmButton
                label="Benutzer löschen"
                question={`"${user.username}" endgültig löschen?`}
                confirmLabel="Ja, löschen"
                disabled={deleting || busy}
                onConfirm={() => void remove()}
              />
            )}
            {isSelf && <span className="muted small">Das eigene Konto kann nicht gelöscht werden.</span>}
          </span>
          <span className="form-actions-right">
            <button type="button" className="button button-ghost" onClick={onClose}>
              Abbrechen
            </button>
            <button type="submit" className="button button-primary" disabled={busy || deleting}>
              {busy ? 'Speichern …' : 'Speichern'}
            </button>
          </span>
        </div>
      </form>
    </Modal>
  );
}
